import { Box, Grid } from "@mui/material";
import PageHeader from "components/Header/Page";
import moment from "moment";
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useRef, useState } from "react";
import { activeWorkspacesTableData, fetchSectorList, workspaceGetEnv } from "service/service";
import { CustomizedDataGrid } from "components/customized_data_grid";
import FiltersBar from "./FilterBar";
import { commonFetchSectors, commonHandleDateSelect, getNavigationStateHelper, syncEnvWithOptions } from "utils/adb-common-util";
import PageWrapper from "components/Common/PageWraper";

const WorkspaceNameFormatter = ({ row, column }: any) => (
    <div
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={row[column.key]}
    >
        {row[column.key] === '--' ? '--' : row[column.key]}
    </div>
);

const BasicFormatter = ({ row, column }: any) => (
    <div>{row[column.key] === '--' ? '--' : row[column.key]}</div>
);

const COLUMN_DEFS = [
    { key: 'workspaceID', name: 'Workspace ID' },
    { key: 'workspaceName', name: 'Workspace Name', minWidth: 250, formatter: WorkspaceNameFormatter },
    { key: 'location', name: 'Location' },
    { key: 'environment', name: 'Environment' },
    { key: 'sector', name: 'Sector' },
    { key: 'resourceGroup', name: 'Resource Group' },
    { key: 'subscription', name: 'Subscription' },
    { key: 'teamFlag', name: 'Team Flag', hidden: true },
    { key: 'computeCount', name: 'Compute Count', formatter: BasicFormatter },
    { key: 'dbu', name: 'DBU', formatter: BasicFormatter },
    { key: 'runtime', name: 'Runtime', formatter: BasicFormatter },
    { key: 'cost', name: 'Cost', formatter: BasicFormatter }
];

const defaultColumns = COLUMN_DEFS.filter(c => !c.hidden).map(c => c.name);

const filterTableData = (data: any[]) => {
    return data.map((row) => {
        const filtered: Record<string, any> = {};
        COLUMN_DEFS.forEach(({ key }) => {
            const val = row[key];
            if (val === null || val === undefined) {
                filtered[key] = '--';
            } else if (['dbu', 'cost'].includes(key)) {
                filtered[key] = typeof val === 'number' ? val.toFixed(2) : val;
            } else if (['runtime', 'computeCount'].includes(key)) {
                filtered[key] = typeof val === 'number' ? val.toLocaleString() : val;
            } else {
                filtered[key] = val;
            }
        });
        return filtered;
    });
};

const getColumns = () => {
    return COLUMN_DEFS.map(col => ({
        ...col,
        width: 'auto',
        resizable: true
    }));
};


const ActiveWsTable = () => {
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const navigationState = routerLocation.state as {
        startDate?: string;
        endDate?: string;
        env?: string;
        sector?: string;
        location?: string;
        selectedDateInString?: string;
    } | null;

    const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
    const isCustom = navigationState?.selectedDateInString?.trim() === 'custom';

    const defStartDate = (!isCustom && navigationState?.startDate) || yesterday.clone().subtract(3, 'months').startOf('day').format('YYYY-MM-DD');
    const defEndDate = (!isCustom && navigationState?.endDate) || yesterday.endOf('day').format('YYYY-MM-DD');
    const defDateString = (!isCustom && navigationState?.selectedDateInString) || '3M';

    const DEFAULT_FILTERS = {
        startDate: defStartDate,
        endDate: defEndDate,
        env: navigationState?.env || 'both',
        sector: navigationState?.sector || 'all',
        location: navigationState?.location || 'South Central US',
        selectedDateInString: defDateString
    };
    const [startDate, setStartDate] = useState<string>(DEFAULT_FILTERS.startDate);
    const [endDate, setEndDate] = useState<string>(DEFAULT_FILTERS.endDate);
    const [env, setEnv] = useState(DEFAULT_FILTERS.env);
    const [sector, setSector] = useState(DEFAULT_FILTERS.sector);
    const [sectorName, setSectorName] = useState(DEFAULT_FILTERS.sector);
    const [location, setLocation] = useState(DEFAULT_FILTERS.location);
    const [selectedDateInString, setSelectedDateInString] = useState(
        DEFAULT_FILTERS.selectedDateInString
    );


    const [loader, setLoader] = useState(false);
    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);
    const [activeTable, setActiveTable] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);

    const initialEnvAppliedRef = useRef(false);
    const envRef = useRef(env);

    // Pagination state - initialized separately like LongRunningJobsPage
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [paginationData, setPaginationData] = useState({
        totalElements: 0,
        totalPages: 0
    });
    const [sortingInfo, setSortingInfo] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' | '' }>({ sortBy: '', sortDir: '' });
    const [sectorList, setSectorList] = useState<string[]>([]);

    const fetchSectors = async () => {
        commonFetchSectors(fetchSectorList, setSectorList);
    };

    useEffect(() => {
        fetchSectors();
    }, []);

    const buildApiFilters = () => {
        return [
            ...((sector === 'all' || sector === 'All') ? [] : [{ operator: 'AND', field: 'Sector', value: sector }]),
            ...(env === 'both' ? [] : [{ operator: 'AND', field: 'Environment', value: env }]),
            ...((location === 'All' || location === 'ALL') ? [] : [{ operator: 'AND', field: 'Location', value: location }]),
        ];
    };

    const getApiSortField = () => {
        if (!sortingInfo?.sortBy) return undefined;

        const key = String(sortingInfo.sortBy);
        const cols = getColumns();
        const keyToNameMap: Record<string, string> = {};
        cols.forEach((c: any) => {
            if (c?.key) keyToNameMap[String(c.key)] = c.name || String(c.key);
            if (c?.name) keyToNameMap[String(c.name)] = c.name;
        });

        const fallbackMap: Record<string, string> = {
            dbu: 'DBU',
            runtime: 'Runtime',
            cost: 'Cost',
            computeCount: 'Compute Count'
        };

        return keyToNameMap[key] || fallbackMap[key];
    };

    const getNavigationState = (overrides = {}) =>
        getNavigationStateHelper({ startDate, endDate, env, sector, location, selectedDateInString }, overrides);

    const handleExportRequest = async () => {
        try {
            const apiFilters = buildApiFilters();
            const apiSortBy = getApiSortField();

            const exportPage = 1;
            const exportPageSize = 10000;

            const resp: any = await activeWorkspacesTableData({
                startDate,
                endDate,
                selectedDateInString,
                sector,
                location,
                page: exportPage,
                pageSize: exportPageSize,
                filters: apiFilters,
                workspaceName: undefined,
                searchText: undefined,
                exportType: 'excel',
                sortField: apiSortBy,
                sortOrder: sortingInfo.sortDir || undefined
            });

            if (!resp || resp?.success === false) {
                console.error('Export failed', resp?.error || resp);
            }
        } catch (err) {
            console.error('Export error', err);
        }
    };

    useEffect(() => { envRef.current = env; }, [env]);
    const handleDateSelect = (stDt: string, edDt: string, selectedDate: string = 'Hrs') => {
        commonHandleDateSelect(stDt, selectedDate, setStartDate, setEndDate, setSelectedDateInString);
    };
    const handleSetSectorName: React.Dispatch<React.SetStateAction<string>> = (v) => {
        const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
        setSectorName(resolved);
        setSector(resolved);
    };

    const getEnvFetch = async (location: string) => {
        try {
            const res = await workspaceGetEnv(location);
            const options = [
                { label: 'ALL', value: 'both' },
                ...(res?.data || [])
            ];
            setEnvOptions(options);
            syncEnvWithOptions(options, initialEnvAppliedRef, envRef, navigationState?.env, setEnv);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getEnvFetch(location);
    }, [location]);

    useEffect(() => {
        const fetchData = async () => {
            setLoader(true);
            try {
                const apiFilters = buildApiFilters();
                const apiSortBy = getApiSortField();

                const resp = await activeWorkspacesTableData({
                    startDate,
                    endDate,
                    selectedDateInString: env,
                    sector,
                    location,
                    page,
                    pageSize,
                    filters: apiFilters,
                    workspaceName: undefined,
                    searchText: undefined,
                    exportType: undefined,
                    sortField: apiSortBy,
                    sortOrder: sortingInfo.sortDir || undefined
                });
                if (resp?.success) {
                    const workspaces = resp.data?.workspaces ?? [];
                    setActiveTable(filterTableData(workspaces));
                    setColumns(getColumns());

                    if (resp.data.pagination) {
                        setPage(resp.data.pagination.page || page);
                        setPageSize(resp.data.pagination.pageSize || pageSize);
                        setPaginationData({
                            totalElements: resp.data.pagination.total || 0,
                            totalPages: resp.data.pagination.totalPages || 0
                        });
                    }
                } else {
                    console.error(resp);
                    setActiveTable([]);
                    setPaginationData({ totalElements: 0, totalPages: 0 });
                }
            } catch (error) {
                console.error(error);
                setActiveTable([]);
                setPaginationData({ totalElements: 0, totalPages: 0 });
            } finally {
                setLoader(false);
            }
        };

        fetchData();
    }, [startDate, endDate, env, sector, location, selectedDateInString, page, pageSize, sortingInfo]);

    const handleSorting = (newSorting: { sortBy: string; sortDir: 'asc' | 'desc' | '' }) => {
        if (newSorting?.sortBy && (newSorting.sortDir === 'asc' || newSorting.sortDir === 'desc')) {
            // store the column key for UI/header matching
            setSortingInfo({
                sortBy: newSorting.sortBy,
                sortDir: newSorting.sortDir || ''
            });
        } else {
            setSortingInfo({ sortBy: '', sortDir: '' });
        }
    };


    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setPageSize(Number(event.target.value));
        setPage(1); // Reset to first page when page size changes
    };

    const updateHistoryState = (overrides: Partial<{
        startDate: string;
        endDate: string;
        env: string;
        sector: string;
        location: string;
        selectedDateInString: string;
    }>) => {
        navigate(routerLocation.pathname, {
            state: getNavigationState(overrides),
            replace: true
        });
    };

    return (
        <PageWrapper>
            <PageHeader
                title={[
                    {
                        text: 'Workspace Overview',
                        link: '/databricksADB/workspace',
                        state: getNavigationState()
                    },
                    {
                        text: 'Active Workspaces Details'
                    }
                ]}
                isPepwise2Breadcrumb={true}
            />

            <FiltersBar
                location={location}
                // pass the actual useState setters (not single-arg wrappers)
                setLocation={setLocation}
                env={env}
                setEnv={setEnv}
                envOptions={envOptions}
                setEnvOptions={setEnvOptions}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleDateSelect={handleDateSelect}
                selectedDateInString={selectedDateInString}
                setSelectedDateInString={setSelectedDateInString}
                sectorName={sectorName}
                setSectorName={handleSetSectorName}
                updateHistoryState={updateHistoryState}
                sectorOptionsList={sectorList}
                useHeaderFilters
            />

            <Grid container spacing={2} px={2}>
                <Grid item xs={12}>
                    <CustomizedDataGrid
                        title="Active Workspaces"
                        tableColumns={columns}
                        tableData={activeTable}
                        tableType="active-workspaces-details-ver"
                        defaultColumnNames={defaultColumns}
                        page={page}
                        pageSize={pageSize}
                        paginationData={paginationData}
                        listLength={activeTable.length}
                        handlepageSizeChange={handlePageSizeChange}
                        handlePageChange={handlePageChange}
                        isRenderPagination={true}
                        isDateRangeFilter={false}
                        isDisabled={false}
                        isFetching={loader}
                        showExport={true}
                        exportTableData={handleExportRequest}
                        onSorting={handleSorting}
                        currSorting={sortingInfo}
                    />
                </Grid>
            </Grid>
        </PageWrapper >
    );
};

export default ActiveWsTable;